import axios from "axios";
import { debug, formatSafeErrorMessage } from "../../../../../utils/debugLogger";
import { createOAuthState } from "../../../../../utils/oauthState";
import {
  getWordPressRedirectUri,
  getWordPressClientId,
} from "../../../config/wordpressEnv";
import type {
  WordPressExchangeTokenResponse,
  WordPressOAuthErrorBody,
} from "../types/wordpress.types";

const WP_AUTHORIZE_BASE = "https://public-api.wordpress.com/oauth2/authorize";
const WP_OAUTH_SCOPES = "global";

const CONTENT_API_BASE = process.env.REACT_APP_CONTENT_API_URL || "";

/** Re-export for callbacks (same redirect rules as env + origin fallback). */
export { getWordPressRedirectUri };

/**
 * Build the WordPress.com OAuth2 authorization URL.
 * Mirrors getLinkedInPersonalAuthUrl() — frontend constructs the URL, backend
 * never needs a /connect endpoint.
 */
export const getWordPressAuthorizeUrl = (): string => {
  const clientId = getWordPressClientId();
  if (!clientId?.trim()) {
    throw new Error(
      "REACT_APP_WORDPRESS_CLIENT_ID is not set. Add it to .env.local and restart."
    );
  }
  const redirectUri = getWordPressRedirectUri();
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    state: createOAuthState("wordpress"),
    scope: WP_OAUTH_SCOPES,
  });
  return `${WP_AUTHORIZE_BASE}?${params.toString()}`;
};

/**
 * Redirect the browser to WordPress.com to begin the OAuth2 consent flow.
 * Caller is responsible for storing the Cognito token before invoking this
 * (e.g. localStorage.setItem("wordpress_pending_token", idToken)) so the
 * callback page can recover it after the redirect.
 */
export const initiateWordPressOAuth = (): void => {
  window.location.href = getWordPressAuthorizeUrl();
};

/**
 * Exchange the WordPress.com authorization code for tokens via the backend.
 *
 * Mirrors exchangeLinkedInCodeForToken() in socialTokenService exactly:
 *  - customer_id travels in the URL path
 *  - Cognito token goes in the Authorization header
 *  - Backend exchanges the code, fetches all WordPress.com sites, stores one
 *    token per site in Secrets Manager, fires a TokensStored EventBridge event
 *  - Response contains site metadata only — access token is never sent to the
 *    browser
 *
 * @param code        Authorization code from WordPress.com callback
 * @param customerId  Customer ID — determines which Secrets Manager path is used
 * @param idToken     Cognito ID token for the Authorization header
 * @param redirectUri The exact redirect URI used when building the auth URL
 */
export const handleWordPressCallback = async (
  code: string,
  customerId: string,
  idToken: string,
  redirectUri?: string
): Promise<WordPressExchangeTokenResponse> => {
  if (!code?.trim()) {
    throw new Error("Authorization code is required");
  }
  if (!customerId?.trim()) {
    throw new Error("Customer ID is required for WordPress token exchange");
  }
  if (!idToken?.trim()) {
    throw new Error("Authentication token is required");
  }
  if (!CONTENT_API_BASE) {
    throw new Error(
      "REACT_APP_CONTENT_API_URL is not set. Add it to .env.local and restart."
    );
  }

  const resolvedRedirectUri = redirectUri?.trim() || getWordPressRedirectUri();
  const endpoint = `${CONTENT_API_BASE}/${customerId}/wordpress/oauth/exchange-token`;

  try {
    debug.log("WordPress token exchange: start", {
      customerId,
      redirectUri: resolvedRedirectUri,
    });

    const response = await axios.post<WordPressExchangeTokenResponse>(
      endpoint,
      { code: code.trim(), redirect_uri: resolvedRedirectUri },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    if (!response.data?.success) {
      debug.error("WordPress token exchange: backend returned success=false", response.data);
      throw new Error(
        response.data?.message || "WordPress token exchange failed"
      );
    }

    debug.log("WordPress token exchange: success", {
      sitesConnected: response.data.connected_sites?.length ?? 0,
    });

    return response.data;
  } catch (error: unknown) {
    debug.error(
      "WordPress token exchange failed:",
      formatSafeErrorMessage(error)
    );

    if (axios.isAxiosError(error)) {
      const data = error.response?.data as WordPressOAuthErrorBody | undefined;
      const status = error.response?.status;

      if (status === 400) {
        throw new Error(
          data?.error_description || data?.error || data?.message ||
          "Missing code or customer_id"
        );
      }
      if (status === 401) {
        throw new Error(
          data?.error || data?.message || "Missing or invalid Cognito token"
        );
      }
      if (status === 500) {
        throw new Error(
          data?.error || data?.message || "WordPress client configuration is incomplete"
        );
      }
      if (status === 502) {
        throw new Error(
          data?.error || data?.error_description || data?.message ||
          "Failed to exchange code with WordPress.com"
        );
      }

      const desc = typeof data?.error_description === "string" ? data.error_description : "";
      const errCode = typeof data?.error === "string" ? data.error : "";
      if (errCode || desc) {
        throw new Error([errCode, desc].filter(Boolean).join(": "));
      }
    }

    if (error instanceof Error) throw error;
    throw new Error("Failed to exchange WordPress authorization code");
  }
};
